import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import styled from 'styled-components'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import { Locations } from 'shared/Router/Locations'
import MerchantConnectionList, {
  merchantConnectionPath,
} from './MerchantConnectionList'
import MerchantConnectionPanel from './MerchantConnectionPanel'
import {
  ConnectionSettings,
  readConnectionSettings,
  readLaunchConnections,
  writeConnectionSettings,
} from './reportedUsageConnections'
import { programMerchantChoices } from './reportedUsageUpload'
import { useReportedUsageMerchants } from './useReportedUsageMerchants'

const Container = styled.div`
  padding: 2rem;
`

const BackLink = styled(Link)`
  color: ${palette.primary.pink700};
  text-decoration: none;
  font-size: 0.875rem;

  &:hover {
    text-decoration: underline;
  }
`

const Subtitle = styled(Text)`
  margin-top: 0.75rem;
`

const StatusText = styled(Text)`
  margin-top: 1.5rem;
`

const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1.25rem;
`

const Tab = styled(Link)`
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  border: 1px solid ${palette.grey.grey200};
  background: ${palette.white};
  color: ${palette.text.secondary};

  &[aria-current='page'] {
    border-color: ${palette.primary.pink800};
    background: ${palette.primary.pink100};
    color: ${palette.primary.pink800};
  }

  &:focus-visible {
    outline: 2px solid ${palette.secondary.purple400};
    outline-offset: 2px;
  }
`

const programPath = (launchId: string) =>
  Locations.DashboardProgram.replace(':launchId', encodeURIComponent(launchId))

const connectionsPath = (launchId: string) =>
  Locations.DashboardProgramConnections.replace(
    ':launchId',
    encodeURIComponent(launchId)
  )

const decodeParam = (value?: string) => (value ? decodeURIComponent(value) : '')

export const MerchantConnectionsView = () => {
  const { launchId: rawLaunchId } = useParams<{ launchId: string }>()
  const launchId = decodeParam(rawLaunchId)
  const { merchants, error, loading } = useReportedUsageMerchants(launchId)
  const [settingsByMerchant, setSettingsByMerchant] = useState(
    readLaunchConnections(launchId)
  )

  useEffect(() => {
    setSettingsByMerchant(readLaunchConnections(launchId))
  }, [launchId])

  const choices = programMerchantChoices(merchants || [])

  return (
    <Container data-cy='merchant-connections'>
      <BackLink to={programPath(launchId)}>Back to program</BackLink>
      <Subtitle variant='body1' color={palette.text.secondary}>
        Set up how Fleet receives reports from each merchant on this program.
      </Subtitle>
      {loading ? (
        <StatusText variant='body1' color={palette.text.secondary}>
          Loading connections…
        </StatusText>
      ) : null}
      {error ? (
        <StatusText variant='body1' color={palette.error.red500}>
          {error}
        </StatusText>
      ) : null}
      {!loading && !error && !choices.length ? (
        <StatusText variant='body1' color={palette.text.secondary}>
          This program has no merchants yet.
        </StatusText>
      ) : null}
      {!loading && !error && choices.length ? (
        <MerchantConnectionList
          launchId={launchId}
          merchants={choices}
          settingsByMerchant={settingsByMerchant}
        />
      ) : null}
    </Container>
  )
}

export const MerchantConnectionView = () => {
  const { launchId: rawLaunchId, merchantId: rawMerchantId } = useParams<{
    launchId: string
    merchantId: string
  }>()
  const launchId = decodeParam(rawLaunchId)
  const merchantId = decodeParam(rawMerchantId)
  const { merchants, error, loading } = useReportedUsageMerchants(launchId)
  const [settings, setSettings] = useState<ConnectionSettings>(() =>
    readConnectionSettings(launchId, merchantId)
  )

  useEffect(() => {
    setSettings(readConnectionSettings(launchId, merchantId))
  }, [launchId, merchantId])

  const choices = programMerchantChoices(merchants || [])
  const selected = choices.find((item) => item.id === merchantId)

  const update = (next: ConnectionSettings) => {
    setSettings(next)
    writeConnectionSettings(launchId, merchantId, next)
  }

  return (
    <Container data-cy='merchant-connection'>
      <BackLink to={connectionsPath(launchId)}>Back to connections</BackLink>
      {!loading && choices.length > 1 ? (
        <Tabs>
          {choices.map((merchant) => (
            <Tab
              key={merchant.id}
              to={merchantConnectionPath(launchId, merchant.id)}
              aria-current={merchant.id === merchantId ? 'page' : undefined}
            >
              {merchant.label}
            </Tab>
          ))}
        </Tabs>
      ) : null}
      {loading ? (
        <StatusText variant='body1' color={palette.text.secondary}>
          Loading connections…
        </StatusText>
      ) : null}
      {error ? (
        <StatusText variant='body1' color={palette.error.red500}>
          {error}
        </StatusText>
      ) : null}
      {!loading && !error && !selected ? (
        <StatusText variant='body1' color={palette.text.secondary}>
          This merchant is not on this program.
        </StatusText>
      ) : null}
      {!loading && !error && selected ? (
        <MerchantConnectionPanel
          launchId={launchId}
          merchantId={merchantId}
          settings={settings}
          onChange={update}
        />
      ) : null}
    </Container>
  )
}
